import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

/** Native dialog cart, with Shopify-rendered prices and a stable hydration boundary. */
class SafeWorldCartDrawer extends Component {
  #abort;
  #opener;
  #updating = false;
  get dialog() { return this.querySelector('dialog'); }
  get root() { return window.Shopify?.routes?.root || '/'; }

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal };
    document.addEventListener('click', this.#handleClick, options);
    this.addEventListener('change', this.#handleChange, options);
    this.dialog.addEventListener('close', this.#handleClose, options);
    window.addEventListener('pageshow', this.#handlePageShow, options);
    this.#syncCount();
  }
  disconnectedCallback() {
    this.#abort?.abort();
    document.body.classList.remove('sw-cart-open');
    super.disconnectedCallback();
  }
  show(opener) {
    this.#opener = opener;
    if (!this.dialog.open) this.dialog.showModal();
    document.body.classList.add('sw-cart-open');
  }
  #handleClose = () => {
    document.body.classList.remove('sw-cart-open');
    if (this.#opener?.isConnected) this.#opener.focus();
  };
  #handlePageShow = event => {
    if (event.persisted) this.refresh().catch(() => {});
  };
  #syncCount() {
    const content = this.querySelector('[data-item-count]');
    document.querySelectorAll('[data-cart-count]').forEach(node => { node.textContent = content.dataset.itemCount; });
    document.querySelectorAll('[data-cart-open]').forEach(node => { node.setAttribute('aria-label', content.dataset.cartLabel); });
  }
  async refresh() {
    const url = new URL(`${this.root}cart`, location.origin);
    url.searchParams.set('sections', this.dataset.sectionId);
    const response = await fetch(url, { signal: this.#abort.signal });
    if (!response.ok) throw new Error(this.dataset.errorMessage);
    const sections = await response.json();
    if (!sections[this.dataset.sectionId]) throw new Error(this.dataset.errorMessage);
    await morphSection(this.dataset.sectionId, sections[this.dataset.sectionId], { mode: 'hydration' });
    this.#syncCount();
  }
  #handleClick = event => {
    const opener = event.target.closest('[data-cart-open]');
    if (opener) {
      event.preventDefault();
      this.show(opener);
      return;
    }
    if (!this.contains(event.target)) return;
    if (event.target.closest('[data-cart-close]')) this.dialog.close();
    if (event.target === this.dialog) {
      const bounds = this.dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
    }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) { event.preventDefault(); this.#change(remove.closest('[data-line-key]'), 0, remove); }
    const button = event.target.closest('[data-drawer-quantity]');
    if (button) {
      const line = button.closest('[data-line-key]');
      const input = line.querySelector('[data-drawer-input]');
      const min = Number(input.min) || 1;
      const max = input.max ? Number(input.max) : Infinity;
      const quantity = Math.max(min, Math.min(max, Number(input.value) + Number(button.dataset.drawerQuantity) * (Number(input.step) || 1)));
      if (quantity !== Number(input.value)) this.#change(line, quantity, button);
    }
  };
  #handleChange = event => {
    if (event.target.matches('[data-drawer-input]')) {
      if (!event.target.checkValidity()) { event.target.reportValidity(); return; }
      this.#change(event.target.closest('[data-line-key]'), Number(event.target.value), event.target);
    }
  };
  async #change(line, quantity, focused) {
    if (this.#updating) return;
    this.#updating = true;
    const key = line.dataset.lineKey;
    const selector = focused.matches('[data-cart-remove]') ? '[data-cart-remove]' : focused.matches('[data-drawer-input]') ? '[data-drawer-input]' : `[data-drawer-quantity="${focused.dataset.drawerQuantity}"]`;
    const error = this.querySelector('.sw-drawer-error');
    error.textContent = '';
    this.dialog.setAttribute('aria-busy', 'true');
    this.querySelectorAll('button,input').forEach(node => { node.disabled = true; });
    try {
      const response = await fetch(`${this.root}cart/change.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }), signal: this.#abort.signal
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || this.dataset.errorMessage);
      await this.refresh();
      const current = this.querySelector(`[data-line-key="${CSS.escape(key)}"] ${selector}`);
      (current || this.querySelector('[data-cart-close]'))?.focus();
    } catch (error) {
      if (error.name !== 'AbortError') this.querySelector('.sw-drawer-error').textContent = error.message || this.dataset.errorMessage;
    } finally {
      this.#updating = false;
      this.dialog.removeAttribute('aria-busy');
      this.querySelectorAll('button,input').forEach(node => { node.disabled = false; });
    }
  }
}
if (!customElements.get('sw-cart-drawer')) customElements.define('sw-cart-drawer', SafeWorldCartDrawer);
